import {
  Masonry,
  type MasonryGroup,
  type MasonryItem,
  notify,
  useConfirmModal,
} from '@affine/component';
import {
  pushGlobalLoadingEventAtom,
  resolveGlobalLoadingEventAtom,
} from '@affine/component/global-loading';
import { DocsService } from '@affine/core/modules/doc';
import {
  exportDocsWithSnapshotExportScope,
  resolveSnapshotExportScope,
} from '@affine/core/modules/doc/services/snapshot-export-scope-resolver';
import { WorkspacePresenceStore } from '@affine/core/modules/organize';
import {
  filterExplorerGroupsForHiddenMailFolderDocIds,
  useHiddenMailFolderDocIds,
} from '@affine/core/modules/streamlain/mail/mail-folder-visibility-filter';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { WorkspacePropertyService } from '@affine/core/modules/workspace-property';
import { Trans, useI18n } from '@affine/i18n';
import type { Store } from '@blocksuite/affine/store';
import { cssVarV2 } from '@streamlain/theme/v2';
import { useLiveData, useService } from '@toeverything/infra';
import { useSetAtom } from 'jotai';
import { nanoid } from 'nanoid';
import {
  type ComponentType,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { EmptyDocs } from '../../affine/empty';
import { useAttachmentSizeDialog } from '../../hooks/affine/use-attachment-size-dialog';
import { useTrashDoc } from '../../hooks/affine/use-trash-doc';
import { ListFloatingToolbar } from '../../page-list/components/list-floating-toolbar';
import { SystemPropertyTypes } from '../../system-property-types';
import { WorkspacePropertyTypes } from '../../workspace-property-types';
import { DocExplorerContext } from '../context';
import { DocListItem, MixId } from './doc-list-item';
import * as styles from './docs-list.css';

const GroupHeader = memo(function GroupHeader({
  groupId,
  collapsed,
  itemCount,
}: {
  groupId: string;
  collapsed?: boolean;
  itemCount: number;
}) {
  const contextValue = useContext(DocExplorerContext);
  const propertyService = useService(WorkspacePropertyService);
  const allProperties = useLiveData(propertyService.sortedProperties$);
  const groupBy = useLiveData(contextValue.groupBy$);

  const groupType = groupBy?.type;
  const groupKey = groupBy?.key;

  const header = useMemo(() => {
    if (groupType === 'system') {
      const property = groupKey && SystemPropertyTypes[groupKey];
      if (!property) return null;
      const GroupHeader = property.groupHeader;
      if (!GroupHeader) return null;
      return (
        <GroupHeader
          groupId={groupId}
          docCount={itemCount}
          collapsed={!!collapsed}
        />
      );
    } else if (groupType === 'property') {
      const property = allProperties.find(p => p.id === groupKey);
      if (!property) return null;

      const config = WorkspacePropertyTypes[property.type];
      if (!config?.groupHeader) return null;
      return (
        <config.groupHeader
          groupId={groupId}
          docCount={itemCount}
          collapsed={!!collapsed}
        />
      );
    } else {
      console.warn('unsupported group type', groupType);
      return null;
    }
  }, [allProperties, collapsed, groupId, groupKey, groupType, itemCount]);

  if (!groupType) {
    return null;
  }

  return header;
});

const ratios = [1.26, 1.304, 1.13, 1.391, 1.521];
const calcCardRatioById = (id: string) => {
  if (!id) {
    return ratios[0];
  }
  const code = id.charCodeAt(0);
  return ratios[code % ratios.length];
};

function isExplorerSelectionPreservingTarget(
  target: EventTarget | null,
  root: HTMLElement | null
) {
  if (!(target instanceof Element)) {
    return false;
  }
  if (target.closest('[data-doc-explorer-selection-ui="true"]')) {
    return true;
  }
  if (!root?.contains(target)) {
    return false;
  }
  return !!target.closest(
    '[data-item-id], [data-doc-id], [data-testid="doc-list-item"]'
  );
}

export type DocListItemComponentProps = {
  groupId: string;
  itemId: string;
};

export const DocListItemComponent = memo(function DocListItemComponent({
  itemId,
  groupId,
}: DocListItemComponentProps) {
  return <DocListItem docId={itemId} groupId={groupId} />;
});

export const DocsExplorer = ({
  className,
  disableMultiSelectToolbar,
  disableMultiDelete,
  masonryItemWidthMin,
  onRestore,
  onDelete,
  prependItems,
  prependSelectableItems,
  resizeDebounce,
  ListItemComponent = DocListItemComponent,
}: {
  className?: string;
  disableMultiSelectToolbar?: boolean;
  disableMultiDelete?: boolean;
  masonryItemWidthMin?: number;
  onRestore?: (ids: string[]) => void;
  /** Override the default delete action */
  onDelete?: (
    ids: string[],
    callbacks?: {
      onFinished?: () => void;
      onAbort?: () => void;
      onError?: (error: Error) => void;
    }
  ) => void;
  /** Extra Masonry items to prepend before doc items (e.g. trashed folders) */
  prependItems?: MasonryItem[];
  /** Item ids represented inside prepended custom items, in visual order. */
  prependSelectableItems?: string[];
  /** Delay before recalculating item geometry after the explorer resizes. */
  resizeDebounce?: number;
  /** Optional replacement for document list rows in list view. */
  ListItemComponent?: ComponentType<DocListItemComponentProps>;
}) => {
  const t = useI18n();
  const contextValue = useContext(DocExplorerContext);
  const docsService = useService(DocsService);
  const trashDoc = useTrashDoc();
  const workspaceService = useService(WorkspaceService);
  const workspace = workspaceService.workspace;
  const presenceStore = useService(WorkspacePresenceStore);
  const pushGlobalLoadingEvent = useSetAtom(pushGlobalLoadingEventAtom);
  const resolveGlobalLoadingEvent = useSetAtom(resolveGlobalLoadingEventAtom);
  const explorerRootRef = useRef<HTMLDivElement>(null);

  const groupBy = useLiveData(contextValue.groupBy$);
  const groups = useLiveData(contextValue.groups$);
  const view = useLiveData(contextValue.view$);
  const selectMode = useLiveData(contextValue.selectMode$);
  const selectedDocIds = useLiveData(contextValue.selectedDocIds$);
  const collapsedGroups = useLiveData(contextValue.collapsedGroups$);
  const hiddenMailFolderDocIds = useHiddenMailFolderDocIds();
  const visibleGroups = useMemo(
    () =>
      filterExplorerGroupsForHiddenMailFolderDocIds(
        groups,
        hiddenMailFolderDocIds
      ),
    [groups, hiddenMailFolderDocIds]
  );

  useEffect(() => {
    if (hiddenMailFolderDocIds.size === 0 || selectedDocIds.length === 0) {
      return;
    }
    const visibleSelectedDocIds = selectedDocIds.filter(
      docId => !hiddenMailFolderDocIds.has(docId)
    );
    if (visibleSelectedDocIds.length !== selectedDocIds.length) {
      contextValue.selectedDocIds$.next(visibleSelectedDocIds);
    }
  }, [contextValue.selectedDocIds$, hiddenMailFolderDocIds, selectedDocIds]);

  const { openConfirmModal } = useConfirmModal();
  const showAttachmentDialog = useAttachmentSizeDialog();

  useEffect(() => {
    contextValue.actionOverrides$.next({ onRestore, onDelete });
    return () => {
      contextValue.actionOverrides$.next({});
    };
  }, [contextValue.actionOverrides$, onDelete, onRestore]);

  useEffect(() => {
    if (workspace.flavour === 'local') return;
    presenceStore.startListening(workspace.id);
  }, [presenceStore, workspace.flavour, workspace.id]);

  const masonryItems = useMemo(() => {
    const docGroups = visibleGroups.map((group: any) => {
      return {
        id: group.key,
        Component: groupBy ? GroupHeader : undefined,
        height: groupBy ? 24 : 0,
        className: styles.groupHeader,
        items: group.items.map((docId: string) => {
          if (view === 'list') {
            return {
              id: docId,
              Component: ListItemComponent,
              height: 42,
            } satisfies MasonryItem;
          }
          return {
            id: docId,
            Component: DocListItemComponent,
            ratio: view === 'grid' ? ratios[0] : calcCardRatioById(docId),
          } satisfies MasonryItem;
        }),
      } satisfies MasonryGroup;
    });

    // Inject prepended items (e.g. trashed folders) into the first group
    // so they share the same Masonry grid layout and spacing as doc rows.
    if (prependItems && prependItems.length > 0) {
      if (docGroups.length > 0) {
        docGroups[0] = {
          ...docGroups[0],
          items: [...prependItems, ...docGroups[0].items],
        };
      } else {
        docGroups.unshift({
          id: '__prepend__',
          Component: undefined,
          height: 0,
          className: styles.groupHeader,
          items: prependItems,
        } satisfies MasonryGroup);
      }
    }

    return docGroups;
  }, [ListItemComponent, groupBy, visibleGroups, view, prependItems]);

  const selectableItems = useMemo(() => {
    const firstGroupId = visibleGroups[0]?.key ?? '__prepend__';
    const resolvedPrependSelectableItems =
      prependSelectableItems ?? (prependItems ?? []).map(item => item.id);
    return [
      ...resolvedPrependSelectableItems.map(itemId => ({
        groupId: firstGroupId,
        itemId,
      })),
      ...visibleGroups.flatMap(group =>
        group.items.map(docId => ({
          groupId: group.key,
          itemId: docId,
        }))
      ),
    ];
  }, [visibleGroups, prependItems, prependSelectableItems]);

  useEffect(() => {
    contextValue.selectableItems$.next(selectableItems);
    return () => {
      contextValue.selectableItems$.next([]);
    };
  }, [contextValue.selectableItems$, selectableItems]);

  useEffect(() => {
    if (!selectMode && selectedDocIds.length === 0) {
      return;
    }

    const onPointerDown = (event: PointerEvent) => {
      if (
        isExplorerSelectionPreservingTarget(
          event.target,
          explorerRootRef.current
        )
      ) {
        return;
      }
      contextValue.selectMode$?.next(false);
      contextValue.selectedDocIds$.next([]);
      contextValue.prevCheckAnchorId$?.next(null);
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [contextValue, selectMode, selectedDocIds.length]);

  const handleCloseFloatingToolbar = useCallback(() => {
    contextValue.selectMode$?.next(false);
    contextValue.selectedDocIds$.next([]);
  }, [contextValue]);

  const handleMultiDelete = useCallback(() => {
    if (disableMultiDelete) {
      handleCloseFloatingToolbar();
      return;
    }
    if (selectedDocIds.length === 0) {
      return;
    }
    const selectedDocIdsSnapshot = [...contextValue.selectedDocIds$.value];
    if (onDelete) {
      onDelete(selectedDocIdsSnapshot, {
        onFinished: () => {
          handleCloseFloatingToolbar();
        },
      });
      return;
    }

    openConfirmModal({
      title: t['com.streamlain.moveToTrash.confirmModal.title.multiple']({
        number: selectedDocIdsSnapshot.length.toString(),
      }),
      description: t[
        'com.streamlain.moveToTrash.confirmModal.description.multiple'
      ]({
        number: selectedDocIdsSnapshot.length.toString(),
      }),
      cancelText: t['com.streamlain.confirmModal.button.cancel'](),
      confirmText: t.Delete(),
      confirmButtonOptions: {
        variant: 'error',
      },
      async onConfirm() {
        const trashTasks: Promise<void>[] = [];
        for (const docId of selectedDocIdsSnapshot) {
          const doc = docsService.list.doc$(docId).value;
          if (doc) {
            trashTasks.push(trashDoc(doc));
          }
        }
        await Promise.all(trashTasks);
      },
    });
  }, [
    contextValue.selectedDocIds$,
    disableMultiDelete,
    docsService.list,
    handleCloseFloatingToolbar,
    onDelete,
    openConfirmModal,
    selectedDocIds.length,
    t,
    trashDoc,
  ]);
  const handleMultiRestore = useCallback(() => {
    const selectedDocIds = contextValue.selectedDocIds$.value;
    onRestore?.(selectedDocIds);
    handleCloseFloatingToolbar();
  }, [
    contextValue.selectedDocIds$.value,
    handleCloseFloatingToolbar,
    onRestore,
  ]);

  // sl-ht07.7: Multi-doc snapshot export for selected docs
  const handleMultiExport = useCallback(() => {
    const docIds = contextValue.selectedDocIds$.value;
    if (docIds.length === 0) return;

    const docCollection = workspace.docCollection;
    const stores: Store[] = [];
    for (const id of docIds) {
      const doc = docCollection.getDoc(id);
      if (doc) {
        stores.push(doc.getStore({ id }));
      }
    }

    if (stores.length === 0) return;

    void resolveSnapshotExportScope({
      docs: stores,
      workspaceService,
      mode: 'multi-doc',
    })
      .then(resolvedScope => {
        // sl-ht07.5: Show attachment dialog before export
        showAttachmentDialog(resolvedScope.stores, ({ excludeBlobIds }) => {
          const globalLoadingID = nanoid();
          pushGlobalLoadingEvent({ key: globalLoadingID });
          exportDocsWithSnapshotExportScope({
            collection: docCollection,
            docs: stores,
            workspaceService,
            mode: 'multi-doc',
            excludeBlobIds,
            resolvedScope,
          })
            .then(() => {
              notify.success({
                title: t['com.streamlain.export.success.title'](),
                message: t['com.streamlain.export.success.message'](),
              });
            })
            .catch(err => {
              console.error(err);
              notify.error({
                title: t['com.streamlain.export.error.title'](),
                message: t['com.streamlain.export.error.message'](),
              });
            })
            .finally(() => {
              resolveGlobalLoadingEvent(globalLoadingID);
              handleCloseFloatingToolbar();
            });
        });
      })
      .catch(err => {
        console.error(err);
        notify.error({
          title: t['com.streamlain.export.error.title'](),
          message: t['com.streamlain.export.error.message'](),
        });
      });
  }, [
    contextValue.selectedDocIds$,
    workspace,
    workspaceService,
    showAttachmentDialog,
    pushGlobalLoadingEvent,
    resolveGlobalLoadingEvent,
    t,
    handleCloseFloatingToolbar,
  ]);

  // Keyboard navigation: Escape to exit, Arrow keys to extend selection
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        contextValue.selectMode$?.next(false);
        contextValue.selectedDocIds$.next([]);
        contextValue.prevCheckAnchorId$?.next(null);
        return;
      }

      // Arrow key navigation in select mode
      if (
        !selectMode ||
        (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') ||
        e.ctrlKey ||
        e.metaKey ||
        e.altKey
      ) {
        return;
      }

      e.preventDefault();

      // Build flat list of all items (same format as handleMultiSelect)
      const flatList = contextValue.selectableItems$.value;
      if (flatList.length === 0) return;

      const anchorMixId = contextValue.prevCheckAnchorId$?.value;
      let currentIndex = -1;

      if (anchorMixId) {
        const { groupId: anchorGroupId, docId: anchorDocId } =
          MixId.parse(anchorMixId);
        currentIndex = flatList.findIndex(
          item => item.groupId === anchorGroupId && item.itemId === anchorDocId
        );
      }

      // If no anchor found, start from top (ArrowDown) or bottom (ArrowUp)
      if (currentIndex === -1) {
        currentIndex = e.key === 'ArrowDown' ? -1 : flatList.length;
      }

      const nextIndex =
        e.key === 'ArrowDown'
          ? Math.min(currentIndex + 1, flatList.length - 1)
          : Math.max(currentIndex - 1, 0);

      // Don't move if already at boundary
      if (nextIndex === currentIndex && currentIndex !== -1) return;

      const nextItem = flatList[nextIndex];
      const nextMixId = MixId.create(nextItem.groupId, nextItem.itemId);

      if (e.shiftKey) {
        // Shift+Arrow: add next item to selection (extend)
        const current = contextValue.selectedDocIds$.value;
        if (!current.includes(nextItem.itemId)) {
          contextValue.selectedDocIds$.next([...current, nextItem.itemId]);
        }
      } else {
        // Arrow without shift: move cursor, select only this item
        contextValue.selectedDocIds$.next([nextItem.itemId]);
      }

      contextValue.prevCheckAnchorId$?.next(nextMixId);

      // Scroll the item into view
      const el =
        document.querySelector(`[data-doc-id="${nextItem.itemId}"]`) ??
        document.querySelector(`[data-item-id="${nextItem.itemId}"]`);
      el?.scrollIntoView({ block: 'nearest' });
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [contextValue, selectMode]);

  const responsivePaddingX = useCallback(
    (w: number) => (w > 500 ? 24 : w > 393 ? 20 : 16),
    []
  );

  const isEmpty = masonryItems.length === 0;

  if (isEmpty) {
    return (
      <div ref={explorerRootRef} className={styles.explorerRoot}>
        <EmptyDocs allowCreate={false} style={{ height: '100%' }} />
      </div>
    );
  }

  return (
    <div ref={explorerRootRef} className={styles.explorerRoot}>
      <Masonry
        animateLayout={view === 'list'}
        className={className}
        items={masonryItems}
        gapY={BUILD_CONFIG.isMobileEdition ? 12 : view === 'list' ? 12 : 24}
        gapX={BUILD_CONFIG.isMobileEdition ? 12 : 24}
        groupsGap={12}
        groupHeaderGapWithItems={12}
        columns={view === 'list' ? 1 : undefined}
        itemWidthMin={masonryItemWidthMin ?? 220}
        preloadHeight={100}
        resizeDebounce={resizeDebounce}
        itemWidth={'stretch'}
        virtualScroll
        collapsedGroups={collapsedGroups}
        paddingY={BUILD_CONFIG.isMobileEdition ? 12 : 0}
        paddingX={BUILD_CONFIG.isMobileEdition ? 16 : responsivePaddingX}
      />
      {!disableMultiSelectToolbar || onRestore ? (
        <ListFloatingToolbar
          contentProps={{ 'data-doc-explorer-selection-ui': true }}
          open={!!selectMode}
          onDelete={disableMultiDelete ? undefined : handleMultiDelete}
          onRestore={onRestore ? handleMultiRestore : undefined}
          onExport={onRestore ? undefined : handleMultiExport}
          onClose={handleCloseFloatingToolbar}
          content={
            <Trans
              i18nKey="com.streamlain.page.toolbar.selected"
              count={selectedDocIds.length}
            >
              <div style={{ color: cssVarV2.text.secondary }}>
                {{ count: selectedDocIds.length } as any}
              </div>
              selected
            </Trans>
          }
        />
      ) : null}
    </div>
  );
};
